import { validDays } from "@/validation";
import {
    type CustomerModel,
    type CustomerRequestModel,
    DocumentsOptionEnum,
    type GdprModel,
    type UpdateCustomerRequestModel,
    type GetCustomerResponseModel,
} from "@/models";
import dayjs from "dayjs";
import { defineStore } from "pinia";
import { SmokerStatusEnum, TitleEnum } from "@/enums";

export const useCustomerStore = defineStore("customer", {
    state: (): CustomerModel => {
        return {
            customerID: 1,
            customerSecret: "",
            title: TitleEnum.Blank,
            firstname: "",
            middlename: "",
            surname: "",
            email: "",
            ncEmail: true,
            dobDate: "",
            dobMonth: "",
            dobYear: "",
            smoker: undefined,
            selectedAddress: null,
            address: {
                line1: "",
                line2: "",
                line3: "",
                line4: "",
                line5: "",
                postcode: "",
            },
            dayPhone: undefined,
            fbo: false,
            documentsOption: DocumentsOptionEnum.Online,
            gdpr: {
                email: true,
                phone: false,
                post: false,
                sms: false,
                personalMarketing: false,
            },
            caciData: {
                cr_addurn: "",
                cr_indurn: "",
                match_attrs: {},
                postcode: "",
                segment_info: {
                    Key: 0,
                    Segment: 0,
                    SubSegment: "",
                },
                uid: "",
            },
        };
    },
    getters: {
        haveEnoughToGetDeals(state): boolean {
            return (
                this.fullDob !== null &&
                state.smoker !== undefined &&
                this.age >= 50 &&
                this.age <= 80
            );
        },
        longDob(state): string | null {
            if (
                state.dobYear !== "" &&
                state.dobMonth !== "" &&
                state.dobDate !== ""
            ) {
                const date = dayjs(
                    `${state.dobYear}-${state.dobMonth}-${state.dobDate}`,
                    "YYYY-MM-DD",
                    true
                );
                if (
                    date.isValid() &&
                    validDays(+state.dobDate, +state.dobMonth, +state.dobYear)
                ) {
                    const monthNames = [
                        "January",
                        "February",
                        "March",
                        "April",
                        "May",
                        "June",
                        "July",
                        "August",
                        "September",
                        "October",
                        "November",
                        "December",
                    ];
                    return `${state.dobDate} ${monthNames[Number(+state.dobMonth - 1)]} ${state.dobYear}`;
                }
            }
            return null;
        },
        fullDob(state): string | null {
            if (
                state.dobYear !== "" &&
                state.dobMonth !== "" &&
                state.dobDate !== ""
            ) {
                const date = dayjs(
                    `${state.dobYear}-${state.dobMonth}-${state.dobDate}`,
                    "YYYY-MM-DD",
                    true
                );
                if (
                    date.isValid() &&
                    validDays(+state.dobDate, +state.dobMonth, +state.dobYear)
                ) {
                    return dayjs(date).format("YYYY-MM-DD");
                }
            }
            return null;
        },
        age(state): number {
            if (
                state.dobYear !== "" &&
                state.dobMonth !== "" &&
                state.dobDate !== ""
            ) {
                const date = dayjs(
                    `${state.dobYear}-${state.dobMonth}-${state.dobDate}`,
                    "YYYY-MM-DD",
                    true
                );
                if (date.isValid()) {
                    const thisAge = dayjs().diff(date, "years");
                    return thisAge;
                }
            }
            return 0;
        },
    },
    actions: {
        createCustomerRequest(): CustomerRequestModel {
            return {
                tracking: false,
                title: this.title === TitleEnum.Blank ? undefined : this.title,
                age: this.age,
                dob: this.fullDob,
                DOBDate: this.dobDate,
                DOBMonth: this.dobMonth,
                DOBYear: this.dobYear,
                firstname: this.firstname,
                middlename: this.middlename,
                surname: this.surname,
                smoker: this.smoker || SmokerStatusEnum.NoneAtAll,
                contacts: {
                    email: this.email,
                    phone: this.dayPhone !== "" ? this.dayPhone : undefined,
                },
                address: this.address.postcode
                    ? {
                          line1: this.address.line1,
                          line2: this.address.line2,
                          line3: this.address.line3,
                          line4: this.address.line4,
                          line5: this.address.line5,
                          postcode: this.address.postcode.toUpperCase(),
                      }
                    : undefined,
                optIn: this.ncEmail,
            };
        },
        updateCustomerRequest(): UpdateCustomerRequestModel {
            return {
                ...this.createCustomerRequest(),
                optIn: [
                    ...Object.keys(this.gdpr)
                        .filter(
                            (key) => this.gdpr[key as keyof GdprModel] === true
                        )
                        .map((key) => key.toLowerCase()),
                ],
            };
        },
        preloadCustomerData(customer: GetCustomerResponseModel): void {
            this.title = customer.title || TitleEnum.Blank;
            this.firstname = customer.firstname || "";
            this.middlename = customer.middlename || "";
            this.surname = customer.surname || "";
            this.smoker = customer.smoker;
            this.email = customer.contacts?.email || "";
            this.dayPhone = customer.contacts?.phone;
            this.address = {
                line1: customer.address?.line1 || "",
                line2: customer.address?.line2 || "",
                line3: customer.address?.line3 || "",
                line4: customer.address?.line4 || "",
                line5: customer.address?.line5 || "",
                postcode: customer.address?.postcode || "",
            };
            if (customer.dob) {
                const date = dayjs(customer.dob, "YYYY-MM-DD", true);
                if (date.isValid()) {
                    this.dobYear = date.format("YYYY");
                    this.dobMonth = date.format("MM");
                    this.dobDate = date.format("DD");
                }
            }
        },
    },
});
